import React, { useState, useRef, useEffect } from 'react';

const styles = `
  @keyframes expandForm {
    0% {
      transform: scale(0.95);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const FloatingQueryButton = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    query: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsExpanded(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('http://localhost:3003/api/send-query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          query: ''
        });
        setTimeout(() => {
          setIsExpanded(false);
          setSubmitStatus(null);
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <>
      <style>{styles}</style>
      <div className={`fixed bottom-5 right-1 z-50 transition-all duration-300 ease-in-out ${isExpanded ? 'w-[95%] sm:w-[300px] md:w-[350px] lg:w-[400px]' : ''}`}>
        {isExpanded ? (
          <div 
            ref={formRef}
            className="bg-richblack-800 rounded-lg shadow-[50px_-5px_50px_-5px] shadow-yellow-50 overflow-hidden border border-richblack-700 transform transition-all duration-300 ease-out origin-bottom-right scale-100 opacity-100"
            style={{
              animation: isExpanded ? 'expandForm 0.3s ease-out forwards' : 'none'
            }}
          >
            <div 
              className="bg-richblack-800 text-richblack-5 px-3 sm:px-5 py-3 sm:py-4 flex justify-between items-center cursor-pointer hover:bg-richblack-700"
              onClick={toggleExpand}
            >
              <h2 className="text-lg sm:text-xl font-semibold">Drop us a Query</h2>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transform rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            
            <div className="px-3 sm:px-5 pb-2 pt-2">
              <div className="flex items-center text-richblack-5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" 
                  />
                </svg>
                <div className="ml-3 sm:ml-4">
                  <h3 className="text-base sm:text-lg font-medium">+91 89717 99526</h3>
                  <p className="text-xs sm:text-sm text-richblack-300">Available 24x7 for your queries</p>
                </div>
              </div>
            </div>
            
            <form className="p-3 sm:p-5 pt-0" onSubmit={handleSubmit}>
              <div className="mb-4 sm:mb-5">
                <input 
                  type="text" 
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your name*" 
                  className="w-full p-2.5 sm:p-3 text-sm sm:text-base border border-richblack-700 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-50 bg-richblack-700 text-richblack-5 placeholder:text-richblack-400"
                  required
                />
              </div>

              <div className="mb-4 sm:mb-5">
                <textarea 
                  name="query"
                  value={formData.query}
                  onChange={handleInputChange}
                  placeholder="Type your query here*" 
                  className="w-full h-24 sm:h-32 p-2.5 sm:p-3 text-sm sm:text-base border border-richblack-700 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-yellow-50 bg-richblack-700 text-richblack-5 placeholder:text-richblack-400"
                  required
                />
              </div>
              
              <div className="mb-4 sm:mb-5">
                <div className="flex">
                  <div className="flex items-center border border-richblack-700 border-r-0 rounded-l-md px-2 sm:px-3 bg-richblack-700">
                    <select className="border-0 bg-transparent mr-1 text-sm sm:text-base text-richblack-5 focus:outline-none" defaultValue="IN">
                      <option value="IN">IN</option>
                    </select>
                    <span className="text-sm sm:text-base text-richblack-5">+91</span>
                  </div>
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter Phone Number*" 
                    className="flex-1 p-2.5 sm:p-3 text-sm sm:text-base border border-richblack-700 rounded-r-md focus:outline-none focus:ring-2 focus:ring-yellow-50 bg-richblack-700 text-richblack-5 placeholder:text-richblack-400"
                    required
                  />
                </div>
              </div>
              
              <div className="mb-4 sm:mb-5">
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email*" 
                  className="w-full p-2.5 sm:p-3 text-sm sm:text-base border border-richblack-700 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-50 bg-richblack-700 text-richblack-5 placeholder:text-richblack-400"
                  required
                />
              </div>

              {submitStatus === 'success' && (
                <div className="mb-4 p-3 bg-green-500 text-white rounded-md">
                  Query submitted successfully!
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-4 p-3 bg-red-500 text-white rounded-md">
                  Failed to submit query. Please try again.
                </div>
              )}
              
              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-yellow-50 text-richblack-900 border-0 rounded-md p-3 sm:p-4 text-sm sm:text-base font-bold cursor-pointer uppercase hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-yellow-50 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'SUBMIT QUERY'}
              </button>
            </form>
          </div>
        ) : (
          <button 
            ref={formRef}
            className="bg-richblack-800 text-richblack-5 border border-richblack-700 rounded-md py-3 sm:py-4 px-4 sm:px-5 text-base sm:text-xl font-semibold cursor-pointer flex items-center justify-between transition-all duration-200 shadow-[10px_-5px_50px_-5px] shadow-yellow-50 hover:shadow-[20px_20px_rgba(255,255,255)] hover:bg-richblack-700"
            onMouseEnter={handleMouseEnter}
          >
            <span>Drop us a Query</span>
            <div className="flex items-center ml-2">
              <span className="w-2 h-2 bg-yellow-50 rounded-full inline-block mr-2"></span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
        )}
      </div>
    </>
  );
};

const FloatingQueryButtonWithStyles = () => {
  return (
    <>
      <style>{styles}</style>
      <FloatingQueryButton />
    </>
  );
};

export default FloatingQueryButtonWithStyles;